/**
 * A session's time in the words the page, the email and the studio show.
 *
 * Pure: the Worker and the SPA read the same clock from the same code. A live
 * stores its start as a UTC instant and its host's zone by IANA name, and every
 * label here is that instant read on that zone's wall clock.
 */

/**
 * The zone each Schedule-card timezone option means. The card stores its own
 * display label ("Eastern Time (ET)" and friends), so the label is what we map.
 */
const SCHEDULE_ZONES: Record<string, string> = {
  "eastern time (et)": "America/New_York",
  "central time (ct)": "America/Chicago",
  "mountain time (mt)": "America/Denver",
  "pacific time (pt)": "America/Los_Angeles",
  "london (gmt)": "Europe/London",
  "central european (cet)": "Europe/Paris",
};

/** The room opens this long before a live starts. */
const ROOM_OPENS_EARLY_MS = 10 * 60_000;

/**
 * The IANA zone a stored schedule timezone means: a Schedule-card label is
 * mapped, and an IANA name (what a live stores) is taken as it is. Null when
 * the words name no zone we can read.
 */
export function scheduleZone(timezone: string): string | null {
  const raw = timezone.trim();
  if (!raw) return null;
  const mapped = SCHEDULE_ZONES[raw.toLowerCase()];
  if (mapped) return mapped;
  try {
    // Intl throws a RangeError for a zone it does not know.
    new Intl.DateTimeFormat("en-US", { timeZone: raw }).format(0);
    return raw;
  } catch {
    return null;
  }
}

interface WallParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

/** The wall clock in `tz` at an instant. Throws a RangeError for a zone Intl does not know. */
function wallAt(ms: number, tz: string): WallParts {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(new Date(ms));
  const p: Record<string, number> = {};
  for (const part of parts) p[part.type] = Number(part.value);
  return {
    year: p.year,
    month: p.month,
    day: p.day,
    hour: p.hour,
    minute: p.minute,
    second: p.second,
  };
}

/** How far `tz` is from UTC at an instant, daylight saving included. */
function offsetMs(tz: string, ms: number): number {
  const w = wallAt(ms, tz);
  const whole = Math.floor(ms / 1000) * 1000;
  return Date.UTC(w.year, w.month - 1, w.day, w.hour, w.minute, w.second) - whole;
}

const pad = (n: number) => String(n).padStart(2, "0");

/**
 * The UTC instant ("2026-09-24T23:00:00.000Z") of a wall-clock time in an IANA
 * zone: "2026-09-24" at "19:00" in America/New_York. Null for a date or time
 * that does not exist on a calendar, or a zone Intl does not know.
 */
export function wallTimeToUtc(dateYmd: string, timeHm: string, tz: string): string | null {
  const d = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateYmd.trim());
  const t = /^(\d{1,2}):(\d{2})$/.exec(timeHm.trim());
  if (!d || !t) return null;
  const [year, month, day, hour, minute] = [d[1], d[2], d[3], t[1], t[2]].map(Number);
  if (hour > 23 || minute > 59) return null;
  const naive = Date.UTC(year, month - 1, day, hour, minute);
  const check = new Date(naive);
  // Date.UTC rolls 30 February over into March rather than refusing it.
  if (check.getUTCMonth() !== month - 1 || check.getUTCDate() !== day) return null;
  try {
    // Why: the offset depends on the instant being solved for, so guess with the
    // naive time, then read the offset again at the guess (right across a DST change).
    const guess = naive - offsetMs(tz, naive);
    return new Date(naive - offsetMs(tz, guess)).toISOString();
  } catch {
    return null;
  }
}

/** An instant's date ("2026-09-24") and time ("19:00") on the wall clock in `tz`, or null. */
export function wallClock(atIso: string, tz: string): { date: string; time: string } | null {
  const ms = Date.parse(atIso);
  if (!Number.isFinite(ms)) return null;
  try {
    const w = wallAt(ms, tz);
    return { date: `${w.year}-${pad(w.month)}-${pad(w.day)}`, time: `${pad(w.hour)}:${pad(w.minute)}` };
  } catch {
    return null;
  }
}

interface LabelParts {
  weekday: string;
  month: string;
  day: string;
  time: string;
  zone: string;
}

/**
 * An instant's pieces on the wall clock in `tz`: the weekday and month in
 * `width` words, the time as "7:00 PM", and the zone as "ET" (`shortGeneric`)
 * or "India Standard Time" (`long`). No `tz` reads the browser's own zone.
 * Both zone names checked 18 Sep 2026 at
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat/DateTimeFormat
 * The time is built from the parts so the space before AM is a plain one:
 * newer ICU puts a narrow no-break space there.
 *
 * Why self-contained, and no comments inside: the page's runtime
 * (`shared/live-page.ts`) carries this function's own source to every visitor,
 * so it may reach nothing outside itself.
 */
export function labelParts(
  atIso: string,
  tz: string | undefined,
  width: "long" | "short",
  zoneName: "shortGeneric" | "long" = "shortGeneric",
): LabelParts | null {
  const ms = Date.parse(atIso);
  if (!Number.isFinite(ms)) return null;
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      weekday: width,
      month: width,
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZoneName: zoneName,
    }).formatToParts(new Date(ms));
    const p: Record<string, string> = {};
    for (const part of parts) p[part.type] = part.value;
    return {
      weekday: p.weekday ?? "",
      month: p.month ?? "",
      day: p.day ?? "",
      time: `${p.hour}:${p.minute} ${p.dayPeriod}`,
      zone: p.timeZoneName ?? "",
    };
  } catch {
    return null;
  }
}

/** English keeps its own wording, with the dot between the day and the time; every other language uses Intl alone. */
const inEnglish = (locale: string | undefined) => !locale || locale.split("-")[0].toLowerCase() === "en";

/**
 * The time in a language's own words and order, from one Intl call: `chip` is the short day and month, `long` the
 * full ones, and `day` the weekday alone with the time (for inside a sentence). An unknown tag reads as English.
 * No fixed English glue ("at", the dot), since each language orders its own.
 */
function intlLabel(atIso: string, tz: string, locale: string, kind: "chip" | "long" | "day"): string {
  const ms = Date.parse(atIso);
  if (!Number.isFinite(ms)) return "";
  const width: "short" | "long" = kind === "chip" ? "short" : "long";
  const date = kind === "day" ? { weekday: width } : { weekday: width, month: width, day: "numeric" as const };
  const opts: Intl.DateTimeFormatOptions = { timeZone: tz, ...date, hour: "numeric", minute: "2-digit", timeZoneName: "shortGeneric" };
  try {
    return new Intl.DateTimeFormat(locale, opts).format(new Date(ms));
  } catch {
    try {
      return new Intl.DateTimeFormat("en", opts).format(new Date(ms));
    } catch {
      return "";
    }
  }
}

/** "Thursday, September 24 · 7:00 PM ET". Empty for an unreadable time or zone. */
export function liveLongLabel(atIso: string, tz: string, locale?: string): string {
  if (!inEnglish(locale)) return intlLabel(atIso, tz, locale as string, "long");
  const p = labelParts(atIso, tz, "long");
  return p ? `${p.weekday}, ${p.month} ${p.day} · ${p.time} ${p.zone}` : "";
}

/** "Thu, Sep 24 · 7:00 PM ET". Empty for an unreadable time or zone. */
export function liveChipLabel(atIso: string, tz: string, locale?: string): string {
  if (!inEnglish(locale)) return intlLabel(atIso, tz, locale as string, "chip");
  const p = labelParts(atIso, tz, "short");
  return p ? `${p.weekday}, ${p.month} ${p.day} · ${p.time} ${p.zone}` : "";
}

/** "Thursday at 7:00 PM ET". Empty for an unreadable time or zone. */
export function liveShortLabel(atIso: string, tz: string, locale?: string): string {
  if (!inEnglish(locale)) return intlLabel(atIso, tz, locale as string, "day");
  const p = labelParts(atIso, tz, "long");
  return p ? `${p.weekday} at ${p.time} ${p.zone}` : "";
}

/** A day in the language's own words ("24 de septiembre"), for the sentence that says when a live was. */
export function liveDateIn(atIso: string, tz: string, locale: string): string {
  const ms = Date.parse(atIso);
  if (!Number.isFinite(ms)) return "";
  try {
    return new Intl.DateTimeFormat(locale, { timeZone: tz, dateStyle: "long" }).format(new Date(ms));
  } catch {
    return "";
  }
}

/** When the room opens: ten minutes before the start, as a UTC ISO. Null for an unreadable time. */
export function roomOpensAt(atIso: string): string | null {
  const ms = Date.parse(atIso);
  return Number.isFinite(ms) ? new Date(ms - ROOM_OPENS_EARLY_MS).toISOString() : null;
}

/**
 * The visitor's own time for a live, as the page says it under the button:
 * "Friday 4:30 AM your time, India Standard Time", or "That's your time too."
 * when their clock reads the same as the host's. Empty when either zone is
 * unreadable. `parts` is `labelParts`, handed in rather than reached for,
 * because the page's runtime carries this function's source on its own.
 */
export function localLineWith(
  parts: typeof labelParts,
  atIso: string,
  hostTz: string,
  visitorTz: string,
): string {
  const mine = parts(atIso, visitorTz || undefined, "long", "long");
  const theirs = parts(atIso, hostTz, "long", "long");
  if (!mine || !theirs) return "";
  if (mine.weekday === theirs.weekday && mine.time === theirs.time) return "That's your time too.";
  return `${mine.weekday} ${mine.time} your time${mine.zone ? `, ${mine.zone}` : ""}`;
}

/**
 * The visitor's own time for a live in another language: the time in their zone from one Intl call, set into
 * the language's own sentence (`mine` holds `{when}`), or the language's own "that is your time too" (`same`).
 * Self-contained, with no comments inside, for the same reason as `labelParts`: the page's runtime carries its source.
 */
export function localLineIn(
  atIso: string,
  hostTz: string,
  visitorTz: string,
  lang: string,
  same: string,
  mine: string,
): string {
  const ms = Date.parse(atIso);
  if (!Number.isFinite(ms)) return "";
  try {
    const f = (tz: string | undefined) =>
      new Intl.DateTimeFormat(lang, { timeZone: tz, weekday: "long", hour: "numeric", minute: "2-digit" }).format(new Date(ms));
    const theirs = f(hostTz);
    const mineAt = f(visitorTz || undefined);
    if (theirs === mineAt) return same;
    const zone = new Intl.DateTimeFormat(lang, { timeZone: visitorTz || undefined, timeZoneName: "long" })
      .formatToParts(new Date(ms))
      .find((p) => p.type === "timeZoneName");
    return mine.replace("{when}", zone ? mineAt + ", " + zone.value : mineAt);
  } catch {
    return "";
  }
}

/** The visitor's own time for a live (see `localLineWith`). */
export function localLine(atIso: string, hostTz: string, visitorTz: string): string {
  return localLineWith(labelParts, atIso, hostTz, visitorTz);
}

/**
 * A countdown's four cells, `msLeft` before the start: whole days, then hours,
 * minutes and seconds with two digits each, never below zero. Self-contained
 * for the same reason as `labelParts`.
 */
export function countdownCells(msLeft: number): { d: string; h: string; m: string; s: string } {
  const left = Number.isFinite(msLeft) ? Math.max(0, Math.floor(msLeft / 1000)) : 0;
  return {
    d: String(Math.floor(left / 86400)),
    h: String(Math.floor((left % 86400) / 3600)).padStart(2, "0"),
    m: String(Math.floor((left % 3600) / 60)).padStart(2, "0"),
    s: String(left % 60).padStart(2, "0"),
  };
}

// ── the host's own pages ────────────────────────────────────────────────
/**
 * Whole calendar days from `nowMs` to an instant, both read on the wall clock
 * in `tz`: 0 on the day itself, below zero once the day has gone by. Null for
 * an unreadable time or zone.
 */
export function daysUntil(atIso: string, tz: string, nowMs: number): number | null {
  const at = wallClock(atIso, tz);
  const now = Number.isFinite(nowMs) ? wallClock(new Date(nowMs).toISOString(), tz) : null;
  if (!at || !now) return null;
  return Math.round((Date.parse(`${at.date}T00:00:00Z`) - Date.parse(`${now.date}T00:00:00Z`)) / 86_400_000);
}

/**
 * Where a live stands, as the webinars list says it: "in 6 days", "tomorrow",
 * "today", "live now", "ended", or "not started" once its day has gone by
 * without it. Days are counted on the host's own calendar.
 */
export function liveWhenWords(
  state: "scheduled" | "live" | "ended",
  atIso: string,
  tz: string,
  nowMs: number,
): string {
  if (state === "live") return "live now";
  if (state === "ended") return "ended";
  const days = daysUntil(atIso, tz, nowMs);
  if (days === null) return "";
  if (days < 0) return "not started";
  if (days === 0) return "today";
  if (days === 1) return "tomorrow";
  return `in ${days} days`;
}

/**
 * The day a live happens, as a number that waits for it says it: "today", the
 * weekday within the week ("Thu" or "Thursday"), else the date ("Sep 24" or
 * "September 24"). Empty for an unreadable time or zone.
 */
export function liveDayWord(atIso: string, tz: string, nowMs: number, width: "short" | "long"): string {
  const days = daysUntil(atIso, tz, nowMs);
  const p = labelParts(atIso, tz, width);
  if (days === null || !p) return "";
  if (days === 0) return "today";
  return days > 0 && days < 7 ? p.weekday : `${p.month} ${p.day}`;
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

/**
 * How long until a live, in the two largest units: "6 days, 1 hour", "3 hours,
 * 20 minutes", "12 minutes", "less than a minute". Empty once the time has come.
 */
export function countdownWords(msLeft: number): string {
  if (!Number.isFinite(msLeft) || msLeft <= 0) return "";
  const min = Math.floor(msLeft / 60_000);
  const days = Math.floor(min / 1440);
  const hours = Math.floor((min % 1440) / 60);
  const minutes = min % 60;
  if (days > 0) return hours > 0 ? `${plural(days, "day")}, ${plural(hours, "hour")}` : plural(days, "day");
  if (hours > 0) return minutes > 0 ? `${plural(hours, "hour")}, ${plural(minutes, "minute")}` : plural(hours, "hour");
  return minutes > 0 ? plural(minutes, "minute") : "less than a minute";
}
